import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/data-table/data-table';
import { Pagination } from '@/components/data-table/pagination';
import { ErrorState } from '@/components/state/error-state';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/query-keys';
import { formatRelative } from '@/lib/format';
import { listRoles, listPermissions, getRbacAudit } from '@/features/rbac/api';
import { BulkRoleDialog } from '@/features/rbac/components/bulk-role-dialog';

type Tab = 'roles' | 'permissions' | 'changes';
const TABS: { id: Tab; label: string }[] = [
  { id: 'roles', label: 'Roles' },
  { id: 'permissions', label: 'Permissions' },
  { id: 'changes', label: 'Role changes' },
];

export const Route = createFileRoute('/_authenticated/access/')({
  component: AccessPage,
});

function AccessPage() {
  const [tab, setTab] = useState<Tab>('roles');
  const [bulkOpen, setBulkOpen] = useState(false);

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div className="flex flex-col gap-1.5">
          <span className="wb-eyebrow">Platform</span>
          <h1 className="wb-h1">Access control</h1>
          <p className="wb-sub">Roles, permissions, and the role-change trail.</p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>
          <ShieldCheck />
          Bulk assign role
        </Button>
      </div>

      <div className="wb-tabs" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={cn('wb-tab', tab === t.id && 'active')}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'roles' ? <RolesTab /> : null}
      {tab === 'permissions' ? <PermissionsTab /> : null}
      {tab === 'changes' ? <ChangesTab /> : null}

      <BulkRoleDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}

function RolesTab() {
  const query = useQuery({ queryKey: queryKeys.rbac.roles(), queryFn: listRoles });

  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isLoading)
    return (
      <span className="wb-skeleton" style={{ width: '100%', height: 120, display: 'block' }} />
    );

  const roles = query.data?.roles ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {roles.map((r) => (
        <Card key={r.role}>
          <CardContent>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <Badge tone={roleTone(r.role)}>{r.role}</Badge>
              <span className="wb-small" style={{ color: 'var(--ink-2)' }}>
                {r.user_count} user(s)
              </span>
            </div>
            <p className="wb-small" style={{ marginBottom: 10 }}>
              {r.description}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(r.permissions ?? []).map((p) => (
                <Badge key={p} tone="neutral">
                  {p}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PermissionsTab() {
  const query = useQuery({ queryKey: queryKeys.rbac.permissions(), queryFn: listPermissions });

  const grouped = useMemo(() => {
    const map = new Map<string, { permission: string; description: string }[]>();
    for (const p of query.data?.permissions ?? []) {
      const ns = p.namespace || 'other';
      const bucket = map.get(ns) ?? [];
      bucket.push({ permission: p.permission, description: p.description });
      map.set(ns, bucket);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [query.data]);

  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isLoading)
    return (
      <span className="wb-skeleton" style={{ width: '100%', height: 120, display: 'block' }} />
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {grouped.map(([ns, perms]) => (
        <Card key={ns}>
          <CardContent>
            <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
              {ns}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {perms.map((p) => (
                <div
                  key={p.permission}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
                >
                  <span className="wb-small wb-mono">{p.permission}</span>
                  <span className="wb-tiny" style={{ textAlign: 'right' }}>
                    {p.description}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ChangesTab() {
  const [page, setPage] = useState(1);
  const limit = 50;

  const query = useQuery({
    queryKey: queryKeys.rbac.audit({ page, limit }),
    queryFn: () => getRbacAudit({ page, limit }),
    placeholderData: (prev) => prev,
  });

  type Entry = NonNullable<typeof query.data>['entries'][number];

  const columns: Column<Entry>[] = [
    {
      key: 'when',
      header: 'When',
      render: (e) => <span className="wb-small">{formatRelative(e.created_at)}</span>,
    },
    {
      key: 'target',
      header: 'Target',
      render: (e) => (
        <div>
          <div style={{ fontWeight: 600 }}>{e.target_display_name || 'Unnamed'}</div>
          <div className="wb-tiny wb-mono">{e.target_user_id}</div>
        </div>
      ),
    },
    {
      key: 'change',
      header: 'Change',
      render: (e) => (
        <span className="wb-small">
          <Badge tone="neutral">{e.old_role}</Badge> →{' '}
          <Badge tone={roleTone(e.new_role)}>{e.new_role}</Badge>
        </span>
      ),
    },
    {
      key: 'by',
      header: 'Changed by',
      render: (e) => <span className="wb-small">{e.changed_by_email || e.changed_by}</span>,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (e) => <span className="wb-small">{e.reason || '—'}</span>,
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={query.data?.entries}
        rowKey={(e) => e.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        empty={{ title: 'No role changes recorded' }}
      />
      {query.data && query.data.total > 0 ? (
        <Pagination page={page} limit={limit} total={query.data.total} onPageChange={setPage} />
      ) : null}
    </>
  );
}

function roleTone(role: string): BadgeTone {
  switch (role) {
    case 'admin':
      return 'danger';
    case 'moderator':
      return 'warning';
    case 'editor':
      return 'info';
    default:
      return 'neutral';
  }
}
